import { initSentry as initSharedSentry } from '@reveni-io/easy-peasy/sentry'
import { returnStatuses } from '@reveni-io/reveni-ui'

import {
  allowedTransactionTypes,
  DEFAULT_SENTRY_TRACES_SAMPLE_RATE,
  phoneAttributesByEventName,
  portalTypes,
} from '../constants'

export const saveLocalStorage = (key, value) => {
  try {
    localStorage[key] = JSON.stringify(value)
    // eslint-disable-next-line
  } catch {} //without catch because is to handle errors if the user is in iframe and incognito mode active.
}

export const getLocalStorage = key => {
  try {
    if (localStorage[key]) return JSON.parse(localStorage[key])
    // eslint-disable-next-line
  } catch {} //without catch because is to handle errors if the user is in iframe and incognito mode active.
}

const stringifyBreadcrumbData = value => {
  if (value === undefined || value === null) return undefined
  if (typeof value === 'string') return value

  return JSON.stringify(value)
}

export const enrichXhrBreadcrumb = (breadcrumb, hint) => {
  if (breadcrumb?.category !== 'xhr') return breadcrumb

  return {
    ...breadcrumb,
    data: {
      ...breadcrumb.data,
      requestBody: stringifyBreadcrumbData(hint?.input),
      response: stringifyBreadcrumbData(hint?.xhr?.response),
    },
  }
}

const isValidSampleRate = rate => Number.isFinite(rate) && rate >= 0 && rate <= 1

export const getSentryTracesSampleRate = () => {
  const value = import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE?.trim()
  const rate = value ? Number(value) : Number.NaN

  return isValidSampleRate(rate) ? rate : DEFAULT_SENTRY_TRACES_SAMPLE_RATE
}

export const initSentry = () => {
  if (import.meta.env.VITE_SENTRY_ENVIRONMENT) {
    initSharedSentry({
      dsn: import.meta.env.VITE_SENTRY_URL,
      environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
      release: VERSION,
      tracesSampleRate: getSentryTracesSampleRate(),
      beforeBreadcrumb: enrichXhrBreadcrumb,
    })
  }
}

export const mergeIframeCopies = (customMessages, iframeCopies) => {
  if (!iframeCopies?.lang) return customMessages

  const { lang, overrides } = iframeCopies
  const currentMessages = customMessages?.[lang] || []
  const incomingKeys = Object.keys(overrides || {})

  const untouchedMessages = currentMessages.filter(message => !incomingKeys.includes(message.key))
  const incomingMessages = incomingKeys.map(key => ({ key, message: overrides[key] }))

  return { ...customMessages, [lang]: [...untouchedMessages, ...incomingMessages] }
}

const applyIframeTheme = (context, iframeTheme) => {
  if (!iframeTheme) return context

  return {
    ...context,
    merchantInfo: {
      ...context.merchantInfo,
      theme: { ...context.merchantInfo?.theme, ...iframeTheme },
    },
  }
}

const applyIframeCopies = (context, iframeCopies) => {
  if (!iframeCopies) return context

  return {
    ...context,
    merchantInfo: {
      ...context.merchantInfo,
      preferences: {
        ...context.merchantInfo?.preferences,
        custom_messages: mergeIframeCopies(context.merchantInfo?.preferences?.custom_messages, iframeCopies),
      },
    },
  }
}

export const buildMergedContext = (context, { iframeTheme, iframeCopies }) => {
  const themedContext = applyIframeTheme(context, iframeTheme)
  const mergedContext = applyIframeCopies(themedContext, iframeCopies)

  return mergedContext
}

export const getTheme = (theme, storeTheme) => {
  if (storeTheme) {
    Object.assign(theme, {
      colors: {
        ...theme.colors,
        primary: {
          tone50: storeTheme.primary_brand_color || theme.colors.primary.tone50,
          tone100: storeTheme.primary_brand_color || theme.colors.primary.tone100,
          tone300: theme.colors.primary.tone300,
          tone500: storeTheme.primary_brand_color || theme.colors.primary.tone500,
          tone700: storeTheme.primary_brand_color || theme.colors.primary.tone700,
          tone900: storeTheme.primary_brand_color || theme.colors.primary.tone900,
        },
      },
      title: {
        primary: storeTheme.primary_brand_color || theme.title.primary,
      },
      button: {
        ...theme.button,
        primary: {
          color: storeTheme.button_text_color || theme.button.primary.color,
          backgroundColor: storeTheme.button_background_color || theme.button.primary.backgroundColor,
          borderColor: storeTheme.button_border_color || theme.button.primary.borderColor,
          hover: {
            color: storeTheme.button_text_hover_color || theme.button.primary.hover.color,
            backgroundColor: storeTheme.button_background_hover_color || theme.button.primary.hover.backgroundColor,
            borderColor: storeTheme.button_border_hover_color || theme.button.primary.hover.borderColor,
          },
          disabled: {
            color: storeTheme.button_text_disabled_color || theme.button.primary.disabled.color,
            backgroundColor:
              storeTheme.button_background_disabled_color || theme.button.primary.disabled.backgroundColor,
            borderColor: storeTheme.button_border_disabled_color || theme.button.primary.disabled.borderColor,
          },
        },
        outlined: {
          ...theme.button.outlined,
          color: storeTheme.button_border_color || theme.button.outlined.color,
          borderColor: storeTheme.button_border_color || theme.button.outlined.borderColor,
          hover: {
            color: storeTheme.button_text_hover_color || theme.button.outlined.hover.color,
            backgroundColor: storeTheme.button_background_hover_color || theme.button.outlined.hover.backgroundColor,
            borderColor: storeTheme.button_border_hover_color || theme.button.outlined.hover.borderColor,
          },
          disabled: {
            color: storeTheme.button_text_disabled_color || theme.button.outlined.disabled.color,
            backgroundColor:
              storeTheme.button_background_disabled_color || theme.button.outlined.disabled.backgroundColor,
            borderColor: storeTheme.button_border_disabled_color || theme.button.outlined.disabled.borderColor,
          },
        },
        text: {
          ...theme.button.text,
          color: storeTheme.button_simple_text_color || theme.button.text.color,
          hover: {
            ...theme.button.text.hover,
            color: storeTheme.button_simple_text_hover_color || theme.button.text.hover.color,
          },
          disabled: {
            ...theme.button.text.disabled,
            color: storeTheme.button_simple_text_disabled_color || theme.button.text.disabled.color,
          },
        },
      },
      link: {
        color: storeTheme.link_color || theme.link.color,
        hover: storeTheme.link_hover_color || theme.link.hover,
      },
      splitButton: {
        ...theme.splitButton,
        backgroundColor: storeTheme.split_button_background_color || theme.splitButton.backgroundColor,
        borderColor: storeTheme.split_button_border_color || theme.splitButton.borderColor,
        color: storeTheme.split_button_text_color || theme.splitButton.color,
        iconColor: storeTheme.split_button_icon_color || theme.splitButton.iconColor,
        hover: {
          backgroundColor: storeTheme.split_button_background_hover_color || theme.splitButton.hover.backgroundColor,
          borderColor: storeTheme.split_button_border_hover_color || theme.splitButton.hover.borderColor,
          color: storeTheme.split_button_text_hover_color || theme.splitButton.hover.color,
          iconColor: storeTheme.split_button_icon_hover_color || theme.splitButton.hover.iconColor,
        },
        disabled: {
          backgroundColor:
            storeTheme.split_button_background_disabled_color || theme.splitButton.disabled.backgroundColor,
          borderColor: storeTheme.split_button_border_disabled_color || theme.splitButton.disabled.borderColor,
          color: storeTheme.split_button_text_disabled_color || theme.splitButton.disabled.color,
          iconColor: storeTheme.split_button_icon_disabled_color || theme.splitButton.disabled.iconColor,
        },
        selected: {
          backgroundColor:
            storeTheme.split_button_background_selected_color || theme.splitButton.selected.backgroundColor,
          borderColor: storeTheme.split_button_border_selected_color || theme.splitButton.selected.borderColor,
          color: storeTheme.split_button_text_selected_color || theme.splitButton.selected.color,
          iconColor: storeTheme.split_button_icon_selected_color || theme.splitButton.selected.iconColor,
        },
      },
      variantButton: {
        ...theme.variantButton,
        color: storeTheme.variant_text_color || theme.variantButton.color,
        backgroundColor: storeTheme.variant_background_color || theme.variantButton.backgroundColor,
        borderColor: storeTheme.variant_border_color || theme.variantButton.borderColor,
        hover: {
          color: storeTheme.variant_text_hover_color || theme.variantButton.hover.color,
          backgroundColor: storeTheme.variant_background_hover_color || theme.variantButton.hover.backgroundColor,
          borderColor: storeTheme.variant_border_hover_color || theme.variantButton.hover.borderColor,
        },
        selected: {
          color: storeTheme.variant_text_selected_color || theme.variantButton.selected.color,
          backgroundColor: storeTheme.variant_background_selected_color || theme.variantButton.selected.backgroundColor,
          borderColor: storeTheme.variant_border_selected_color || theme.variantButton.selected.borderColor,
        },
        disabled: {
          color: storeTheme.variant_text_disabled_color || theme.variantButton.disabled.color,
          backgroundColor: storeTheme.variant_background_disabled_color || theme.variantButton.disabled.backgroundColor,
          borderColor: storeTheme.variant_border_disabled_color || theme.variantButton.disabled.borderColor,
        },
      },
    })
  }

  return theme
}

export const replaceAmountParam = url => {
  return url?.replace(/(amount=.*)/, 'amount=:amount')
}

export const getOrderAmount = data => {
  return {
    original_amount: data?.summary?.original_amount,
    fee_amount: data?.summary?.fee_amount,
    fees: data?.summary?.fees,
    summary_total_amount: data?.summary?.summary_total_amount,
    exchange_items_amount: data?.summary?.exchange_items_amount,
    currency: data?.summary?.currency,
    amount: data?.summary?.amount,
    remaining_amount: data?.summary?.remaining_amount,
    reveni_coverage_amount: data?.summary?.reveni_coverage_amount,
    remaining_amount_in_store_currency: data?.summary?.remaining_amount_in_store_currency,
    bonuses: data?.summary?.bonuses,
    bonus_amount: data?.summary?.bonus_amount,
  }
}

export const needsRefund = amount => {
  return Number(amount) > 0
}

export const decipherJwt = jwt => {
  const payload = jwt?.split('.')[1]
  if (payload) {
    return JSON.parse(atob(payload))
  }
}

export const isTokenExpired = token => {
  if (token) {
    const jwt = decipherJwt(token)

    const expiresAt = new Date(jwt.exp * 1000)
    const now = new Date()
    const expireThreshold = 90000 // 1.5 minutes

    return expiresAt - now < expireThreshold
  }
  return false
}

export const getTextForWhenIssueRefund = (t, issueRefundAtStatus) => {
  const texts = {
    [returnStatuses.shipped]: t('cuando envíes tu devolución'),
    [returnStatuses.received]: t('cuando se reciba tu devolución'),
    [returnStatuses.accepted]: t('cuando se acepte tu devolución'),
  }

  return texts[issueRefundAtStatus]
}

export const validateName = name => {
  const regex = /^(?=.{2,})([A-Za-zÀ-ÖØ-öø-ÿ']+(?:[\s-][A-Za-zÀ-ÖØ-öø-ÿ']+)*)$/

  return name && regex.test(name.trim())
}

export const getMerchantHeaderDescriptionText = (transactionTypes = [], portalType, isGiftReturn) => {
  if (isGiftReturn) {
    return 'regalos'
  }

  if (portalType === portalTypes.cancellations) {
    return 'cancelaciones'
  }

  if (portalType === portalTypes.returnToSender) {
    return 'return to sender'
  }

  const hasExchange = transactionTypes.includes(allowedTransactionTypes.exchange)
  const hasInstantRefund = transactionTypes.includes(allowedTransactionTypes.instantRefund)

  if (hasExchange && hasInstantRefund) {
    return 'cambios y devoluciones'
  }

  if (hasExchange) {
    return 'cambios'
  }

  if (hasInstantRefund) {
    return 'devoluciones'
  }

  return ''
}

export const getCustomMessageFormatted = message => {
  return message?.replaceAll('&nbsp;', ' ')
}

export const updateAddressField = (address, name, value) => {
  const phoneAttribute = phoneAttributesByEventName[name]

  if (phoneAttribute) {
    const phoneValue = name === 'phone' ? value.replace(/\s/g, '') : value

    return { ...address, phone: { ...address?.phone, [phoneAttribute]: phoneValue } }
  }

  if (name === 'province') {
    return { ...address, province: { ...address?.province, code: value } }
  }

  return { ...address, [name]: value }
}

export const formatOrderDate = (isoDate, language) => {
  return new Intl.DateTimeFormat(language, { day: 'numeric', month: 'short', year: 'numeric' }).format(
    new Date(isoDate)
  )
}

export const getProductCountLabel = (t, count) => {
  return count === 1 ? t('1 producto') : t('{{count}} productos', { count })
}
